import { FC, useState } from 'react';
import { toast } from 'react-toastify';

import { CloseIcon } from '../../../assets/icons/index';
import InfoCircle from '../../../assets/icons/info-circle';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { useLocale } from '../../../hooks/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { useRequestMoreEnvelopes } from '../hooks/use-request-more-envelopes.hook';
import { eventsModalStore } from '../store/events-modal.store';

export const GetMoreEnvelopesModal: FC = () => {
  const [customMessage, setCustomMessage] = useState('');
  const { messagesLocale } = useLocale();
  const selectedCompany = selectedCompanyStorage(({ company }) => company);
  const isOpen = eventsModalStore(({ open }) => open);
  const handleAccept = eventsModalStore(({ handleAccept }) => handleAccept);
  const handleDismiss = eventsModalStore(({ handleDismiss }) => handleDismiss);

  const { isPending, requestMoreEnvelopes } = useRequestMoreEnvelopes({
    onSuccess: () => {
      setCustomMessage('');
      handleAccept();
    },
  });

  const handleSubmit = () => {
    const companyId = selectedCompany?.id;
    if (!companyId) {
      toast.error(messagesLocale['GET_MORE_ENVELOPES_REQUEST_FAILED']);
      return;
    }

    requestMoreEnvelopes({ companyId, customMessage: customMessage || undefined });
  };

  return (
    <AlertDialogWrapper
      control={{
        onOpenChange: (open) => {
          if (!open) handleDismiss();
        },
        open: isOpen,
      }}
    >
      <div className="m-2 flex w-full max-w-[493px] flex-col gap-4 rounded-lg bg-white p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-xl font-semibold text-gray-700">
              <AppFormattedMessage id="GET_MORE_ENVELOPES" />
            </p>
            <p className="text-xs font-normal text-gray-800">
              <AppFormattedMessage id="GET_MORE_ENVELOPES_MODAL_DESCRIPTION" />
            </p>
          </div>
          <Button
            className="h-9 w-9 shrink-0 rounded-full border border-[#F2F2F2] bg-white"
            onClick={handleDismiss}
            styleType="NONE"
          >
            <CloseIcon className="h-2.5 w-2.5" iconColor="#172335" />
          </Button>
        </div>

        <div className="flex h-11 items-center gap-2 rounded-[4px] border border-brand-200 bg-brand-25 p-3">
          <InfoCircle className="size-5 shrink-0" iconColor="#2563EB" />
          <span className="text-xs text-gray-800">
            <AppFormattedMessage id="GET_MORE_ENVELOPES_INFO_BANNER" />
          </span>
        </div>

        <div className="flex flex-col gap-2 rounded-[4px] border border-gray-200 p-3">
          <label className="text-xs text-gray-700">
            <AppFormattedMessage id="CUSTOM_MESSAGE_OPTIONAL" />
          </label>
          <textarea
            className="min-h-[80px] w-full resize-none rounded-[4px] border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 placeholder:text-gray-300 focus:border-brand-500"
            onChange={(event) => setCustomMessage(event.target.value)}
            placeholder={messagesLocale?.CUSTOM_MESSAGE_PLACEHOLDER ?? ''}
            value={customMessage}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button
            className="w-[94px] border border-gray-200 text-sm"
            onClick={handleDismiss}
            styleType="NONE"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="w-[142px]"
            disabled={isPending}
            onClick={handleSubmit}
            styleType="BLUE_ROUNDED"
          >
            <AppFormattedMessage id="SEND_REQUEST" />
          </Button>
        </div>
      </div>
    </AlertDialogWrapper>
  );
};
